import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap, switchMap, take, shareReplay } from 'rxjs/operators';
import { AwsService, AwsConnection, AwsAccount } from './aws.service';
import { AzureService } from './azure.service';
import { GcpService } from './gcp.service';
import { OrgService, MemberWithUser } from './org.service';
import { GLOBAL_SCOPE, ALL_REGIONS_SCOPE } from '../aws-regions';
import { CloudProvider } from '../cloud-catalog';
import { environment } from '../../../environments/environment';

// ── Monitoring types (mirrors the backend ServiceEntry and QuotaInfo shapes) ──

export type MonitoringServiceType = 'LAMBDA' | 'GLUE' | 'PIPELINE' | 'STEP_FUNCTIONS' | 'ECS_SERVICE' | 'RDS_INSTANCE' | 'API_GATEWAY' | 'EC2_INSTANCE' | 'COGNITO' | 'APPSYNC' | 'DYNAMODB' | 'SQS' | 'EVENTBRIDGE' | 'ALB' | 'SNS' | 'CLOUDFRONT';

export interface ServiceEntry {
  userId: string;
  provider?: CloudProvider; // cloud provider that owns this resource
  serviceType: MonitoringServiceType;
  resourceId: string;
  region?: string;    // region/location scope ('global' for account-scoped services)
  accountId?: string; // AWS account id / Azure subscription id
  isMonitored: boolean;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface QuotaInfo {
  currentCount: number;
  limit: number;
  tier: string;
  remaining: number;
  percentUsed: number;
}

export type HealthStatus = 'healthy' | 'warning' | 'critical' | 'unknown' | 'idle';

/**
 * A discovered Azure resource (Resource Graph row). Shared shape across the
 * Core-6 services — the detail pages render this directly. `id` is the full ARM
 * resource id (the monitored resourceId), `location` is the Azure location.
 */
export interface AzureResource {
  id:             string;
  name:           string;
  location:       string;
  resourceGroup?: string;
  kind?:          string;
  sku?:           string;
  status:         string;
  metadata?:      Record<string, string>;
}

/**
 * A discovered GCP resource. Shared shape across the GCP services — the detail
 * pages render this directly. `id` is the full resource id (the monitored
 * resourceId), `location` is the GCP region ('global' for global services),
 * `project` is the owning project id.
 */
export interface GcpResource {
  id:        string;
  name:      string;
  location:  string;
  project?:  string;
  type?:     string;
  status:    string;
  metadata?: Record<string, string>;
}
export type CloudPageState = 'loading' | 'not-connected' | 'connected' | 'error';
export type ServiceId = 'lambda' | 'glue' | 'codepipeline' | 'step-functions' | 'ecs' | 'rds' | 'api-gateway' | 'ec2' | 'cognito' | 'appsync' | 'dynamodb' | 'sqs' | 'eventbridge' | 'alb' | 'sns' | 'cloudfront';

// ── Destination config types (no enabled flag) ────────────────────────────────

export interface SlackDestination  { webhookUrl: string; }
export interface TeamsDestination  { webhookUrl: string; }
export interface EmailDestination  { recipientIds: string[]; }
export interface GitHubDestination { repository: string; }
export interface JiraDestination   { projectKey: string; }

// ── Config mode ───────────────────────────────────────────────────────────────
// 'inherited' = use org/global default; 'custom' = use this level's own destination

export type ConfigMode = 'inherited' | 'custom';

// ── Service-level channel policy ──────────────────────────────────────────────
// Each channel at service level: enabled/disabled + how the destination is sourced.
// overrideConfig is only required when configMode = 'custom'.

export interface SlackServicePolicy  { enabled: boolean; configMode: ConfigMode; overrideConfig?: SlackDestination;  }
export interface TeamsServicePolicy  { enabled: boolean; configMode: ConfigMode; overrideConfig?: TeamsDestination;  }
export interface EmailServicePolicy  { enabled: boolean; configMode: ConfigMode; overrideConfig?: EmailDestination;  }
export interface GitHubServicePolicy { enabled: boolean; configMode: ConfigMode; overrideConfig?: GitHubDestination; }
export interface JiraServicePolicy   { enabled: boolean; configMode: ConfigMode; overrideConfig?: JiraDestination;   }

export interface ServiceNotificationPolicy {
  slack: SlackServicePolicy;
  teams: TeamsServicePolicy;
  email: EmailServicePolicy;
}

export interface ServiceTicketingPolicy {
  github: GitHubServicePolicy;
  jira:   JiraServicePolicy;
}

export interface ServicePolicy {
  notifications: ServiceNotificationPolicy;
  ticketing:     ServiceTicketingPolicy;
}

// ── Item-level channel mode ───────────────────────────────────────────────────
// 'service'           = defer entirely to service-level policy (no item-level override)
// 'disabled'          = explicitly disabled for this entry regardless of service setting
// 'enabled-inherited' = enabled; resolve destination from service then org
// 'enabled-custom'    = enabled; use a custom destination defined on this entry

export type ItemChannelMode = 'service' | 'disabled' | 'enabled-inherited' | 'enabled-custom';

export interface SlackItemPolicy  { mode: ItemChannelMode; overrideConfig?: SlackDestination;  }
export interface TeamsItemPolicy  { mode: ItemChannelMode; overrideConfig?: TeamsDestination;  }
export interface EmailItemPolicy  { mode: ItemChannelMode; overrideConfig?: EmailDestination;  }
export interface GitHubItemPolicy { mode: ItemChannelMode; overrideConfig?: GitHubDestination; }
export interface JiraItemPolicy   { mode: ItemChannelMode; overrideConfig?: JiraDestination;   }

export interface ItemNotificationPolicy {
  slack: SlackItemPolicy;
  teams: TeamsItemPolicy;
  email: EmailItemPolicy;
}

export interface ItemTicketingPolicy {
  github: GitHubItemPolicy;
  jira:   JiraItemPolicy;
}

export interface ItemPolicy {
  notifications: ItemNotificationPolicy;
  ticketing:     ItemTicketingPolicy;
}

// ── Org-level default destinations ───────────────────────────────────────────
// These are the organization-wide defaults that service and item policies can inherit from.

export interface OrgNotificationDefaults {
  slack?: SlackDestination;
  teams?: TeamsDestination;
  email?: EmailDestination;
}

export interface OrgTicketingDefaults {
  github?: GitHubDestination;
  jira?:   JiraDestination;
}

export interface OrgDefaults {
  notifications: OrgNotificationDefaults;
  ticketing:     OrgTicketingDefaults;
}

// ── Effective/resolved state ──────────────────────────────────────────────────

export type EffectiveSource = 'org' | 'service' | 'item';

export interface ResolvedChannelState<TConfig> {
  enabled:         boolean;
  source:          EffectiveSource | null;
  config:          TConfig | null;
  isConfigMissing: boolean;
}

export interface ResolvedNotifications {
  slack:  ResolvedChannelState<SlackDestination>;
  teams:  ResolvedChannelState<TeamsDestination>;
  email:  ResolvedChannelState<EmailDestination>;
}

export interface ResolvedTicketing {
  github: ResolvedChannelState<GitHubDestination>;
  jira:   ResolvedChannelState<JiraDestination>;
}

export interface ResolvedPolicy {
  notifications: ResolvedNotifications;
  ticketing:     ResolvedTicketing;
}

// ── Existing cloud types ──────────────────────────────────────────────────────

export interface ServiceMetric {
  label: string;
  value: string;
}

export interface ServiceCardData {
  id: string;
  name: string;
  icon: string;
  route: string;
  status: HealthStatus;
  metrics: ServiceMetric[];
  /** 'global' for account-scoped services (e.g. CloudFront) that ignore the region selector. */
  scope?: 'global';
}

export interface CloudSummary {
  accountId: string;         // the currently selected AWS account
  account: string;           // alias of accountId (the active account scope)
  accounts: AwsAccount[];    // all connected AWS accounts for this subscription
  roleArn: string;
  region: string;            // current scope label for back-compat with the summary bar
  scope: string;             // active region scope ('all' | region | 'global')
  defaultRegion: string;     // default region of the SELECTED account
  enabledRegions: string[];  // enabled regions of the SELECTED account
  lastSyncAt: string;
  services: ServiceCardData[];
}

export interface LambdaMetrics {
  invocations: number;
  errors:      number;
  throttles:   number;
}

export interface LambdaHourSlot {
  timestamp:     string;
  invocations:   number;
  errors:        number;
  throttles:     number;
  avgDurationMs: number | null;
}

export interface LambdaFunctionMetrics {
  functionName:     string;
  totalInvocations: number;
  totalErrors:      number;
  totalThrottles:   number;
  slots:            LambdaHourSlot[];
}

export interface LambdaFunction {
  name:           string;
  runtime:        string;
  lastModified:   string;
  memoryMB:       number;
  timeoutSec:     number;
  status:         HealthStatus;
  invocations24h: number;
  errors24h:      number;
  throttles24h:   number;
  region?:        string;
}

export interface GlueJob {
  name:          string;
  type:          string;
  lastRunStatus: 'SUCCEEDED' | 'FAILED' | 'RUNNING' | 'STOPPED' | null;
  lastRunAt:     string | null;
  duration:      string | null;
  region?:       string;
}

export interface GlueJobRun {
  runId:        string;
  status:       string;
  startedAt:    string | null;
  completedAt:  string | null;
  duration:     string | null;
  trigger:      string | null;
  errorMessage: string | null;
}

export interface PipelineExecution {
  executionId:  string;
  status:       string;
  startTime:    string | null;
  lastUpdated:  string | null;
  trigger:      string | null;
}

export interface Pipeline {
  name:            string;
  status:          'Succeeded' | 'Failed' | 'InProgress' | 'Stopped' | 'Unknown';
  lastExecutionAt: string | null;
  failedStage:     string | null;
  region?:         string;
}

export interface StateMachine {
  arn:                 string;
  name:                string;
  type:                'STANDARD' | 'EXPRESS';
  lastExecutionStatus: 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED' | 'RUNNING' | null;
  lastExecutionAt:     string | null;
  region?:             string;
}

export interface EcsService {
  arn:            string;
  name:           string;
  clusterArn:     string;
  clusterName:    string;
  desiredCount:   number;
  runningCount:   number;
  pendingCount:   number;
  status:         string;
  taskDefinition: string;
  launchType:     string;
  healthStatus:   'healthy' | 'warning' | 'unknown';
  region?:        string;
}

export interface RdsInstance {
  dbInstanceIdentifier: string;
  dbInstanceClass:      string;
  engine:               string;
  engineVersion:        string;
  status:               string;
  endpoint:             string | null;
  port:                 number | null;
  multiAZ:              boolean;
  allocatedStorageGiB:  number;
  storageType:          string;
  arn:                  string;
  region?:              string;
}

export interface StateMachineExecution {
  executionArn: string;
  name:         string;
  status:       string;
  startDate:    string | null;
  stopDate:     string | null;
}

export interface EcsServiceEvent {
  id:        string;
  message:   string;
  createdAt: string | null;
}

export interface RdsInstanceEvent {
  message:    string;
  categories: string[];
  eventDate:  string | null;
}

// API Gateway is monitored at the REST API level (CloudWatch ApiName dimension).
// `name` is the monitored resource id; `id` is for display only.
export interface ApiGatewayApi {
  id:           string;
  name:         string;
  description:  string | null;
  protocol:     'REST';
  endpointType: string;
  createdAt:    string | null;
  region?:      string;
}

// EC2 is monitored per individual instance (InstanceId dimension + state events).
export interface Ec2Instance {
  instanceId:       string;
  name:             string | null;
  instanceType:     string;
  state:            string;
  availabilityZone: string | null;
  privateIp:        string | null;
  publicIp:         string | null;
  launchTime:       string | null;
  region?:          string;
}

// Cognito is monitored at the user-pool level (UserPool dimension). `id` is the
// monitored resource id.
export interface CognitoUserPool {
  id:           string;
  name:         string;
  status:       HealthStatus;
  createdAt:    string | null;
  lastModified: string | null;
  region?:      string;
}

// AppSync is monitored at the GraphQL API level (CloudWatch GraphQLAPIId
// dimension). `apiId` is the monitored resource id.
export interface AppSyncApi {
  apiId:              string;
  name:               string;
  authenticationType: string;
  region?:            string;
}

// DynamoDB is monitored at the table level (CloudWatch TableName dimension).
// `name` is the monitored resource id.
export interface DynamoDbTable {
  name:        string;
  status:      string;
  billingMode: string;
  itemCount:   number;
  sizeBytes:   number;
  region?:     string;
}

// SQS is monitored at the queue level (CloudWatch QueueName dimension).
// `name` is the monitored resource identifier. `isDeadLetter` is inferred from
// other queues' redrive policies; `hasDeadLetterQueue` means this queue redrives.
export interface SqsQueue {
  name:               string;
  url:                string;
  arn:                string;
  messagesVisible:    number;
  messagesNotVisible: number;
  isFifo:             boolean;
  hasDeadLetterQueue: boolean;
  isDeadLetter:       boolean;
  region?:            string;
}

// EventBridge is monitored at the rule level (CloudWatch RuleName dimension).
// `name` is the monitored resource identifier.
export interface EventBridgeRule {
  name:         string;
  arn:          string;
  state:        string;        // ENABLED | DISABLED
  eventBusName: string;
  scheduled:    boolean;       // has a schedule expression (vs. event-pattern)
  region?:      string;
}

// ALB is monitored at the load-balancer level (CloudWatch LoadBalancer dimension).
// `name` is that dimension value (e.g. app/my-lb/abc123) — the monitored id.
export interface AlbLoadBalancer {
  name:    string;
  arn:     string;
  dnsName: string;
  scheme:  string;
  state:   string;
  region?: string;
}

// SNS is monitored at the topic level (CloudWatch TopicName dimension).
// `name` is the topic name (monitored id).
export interface SnsTopic {
  name:                  string;
  arn:                   string;
  displayName:           string | null;
  subscriptionsConfirmed: number;
  region?:                string;
}

// CloudFront is monitored at the distribution level (CloudWatch DistributionId
// dimension; metrics live in us-east-1). `id` is the monitored identifier.
export interface CloudFrontDistribution {
  id:         string;
  domainName: string;
  enabled:    boolean;
  status:     string;
  comment:    string;
  region?:    string;
}

// Per-resource CloudWatch metric history (drill-down). `values` is keyed by the
// metric key (e.g. 'e5xx', 'cpu'); null = no datapoint for that hour.
export interface MetricHistorySlot {
  timestamp: string;
  values:    Record<string, number | null>;
}

export interface MetricHistory {
  slots: MetricHistorySlot[];
}

const CONNECTED_SERVICES: ServiceCardData[] = [
  {
    id:      'alb',
    name:    'ALB',
    icon:    'network',
    route:   '/cloud/alb',
    status:  'healthy',
    metrics: [{ label: 'Load Balancers', value: '—' }],
  },
  {
    id:      'api-gateway',
    name:    'API Gateway',
    icon:    'globe',
    route:   '/cloud/api-gateway',
    status:  'healthy',
    metrics: [{ label: 'REST APIs', value: '—' }],
  },
  {
    id:      'appsync',
    name:    'AppSync',
    icon:    'workflow',
    route:   '/cloud/appsync',
    status:  'healthy',
    metrics: [{ label: 'GraphQL APIs', value: '—' }],
  },
  {
    id:      'codepipeline',
    name:    'CodePipeline',
    icon:    'git-branch',
    route:   '/cloud/codepipeline',
    status:  'healthy',
    metrics: [{ label: 'Pipelines', value: '—' }],
  },
  {
    id:      'cloudfront',
    name:    'CloudFront',
    icon:    'cloud',
    route:   '/cloud/cloudfront',
    status:  'healthy',
    metrics: [{ label: 'Distributions', value: '—' }],
    scope:   'global',
  },
  {
    id:      'cognito',
    name:    'Cognito',
    icon:    'users',
    route:   '/cloud/cognito',
    status:  'healthy',
    metrics: [{ label: 'User Pools', value: '—' }],
  },
  {
    id:      'dynamodb',
    name:    'DynamoDB',
    icon:    'table',
    route:   '/cloud/dynamodb',
    status:  'healthy',
    metrics: [{ label: 'Tables', value: '—' }],
  },
  {
    id:      'ec2',
    name:    'EC2',
    icon:    'server',
    route:   '/cloud/ec2',
    status:  'healthy',
    metrics: [{ label: 'Instances', value: '—' }],
  },
  {
    id:      'ecs',
    name:    'ECS',
    icon:    'box',
    route:   '/cloud/ecs',
    status:  'healthy',
    metrics: [{ label: 'Services', value: '—' }],
  },
  {
    id:      'eventbridge',
    name:    'EventBridge',
    icon:    'radio',
    route:   '/cloud/eventbridge',
    status:  'healthy',
    metrics: [{ label: 'Rules', value: '—' }],
  },
  {
    id:      'glue',
    name:    'Glue',
    icon:    'database',
    route:   '/cloud/glue',
    status:  'healthy',
    metrics: [{ label: 'Jobs', value: '—' }],
  },
  {
    id:      'lambda',
    name:    'Lambda',
    icon:    'zap',
    route:   '/cloud/lambda',
    status:  'healthy',
    metrics: [{ label: 'Functions', value: '—' }],
  },
  {
    id:      'rds',
    name:    'RDS',
    icon:    'database',
    route:   '/cloud/rds',
    status:  'healthy',
    metrics: [{ label: 'Instances', value: '—' }],
  },
  {
    id:      'sns',
    name:    'SNS',
    icon:    'megaphone',
    route:   '/cloud/sns',
    status:  'healthy',
    metrics: [{ label: 'Topics', value: '—' }],
  },
  {
    id:      'sqs',
    name:    'SQS',
    icon:    'inbox',
    route:   '/cloud/sqs',
    status:  'healthy',
    metrics: [{ label: 'Queues', value: '—' }],
  },
  {
    id:      'step-functions',
    name:    'Step Functions',
    icon:    'git-graph',
    route:   '/cloud/step-functions',
    status:  'healthy',
    metrics: [{ label: 'State Machines', value: '—' }],
  },
];

// ── Policy defaults (returned when nothing is persisted yet) ─────────────────

const EMPTY_SERVICE_POLICY = (): ServicePolicy => ({
  notifications: {
    slack: { enabled: false, configMode: 'inherited' },
    teams: { enabled: false, configMode: 'inherited' },
    email: { enabled: false, configMode: 'inherited' },
  },
  ticketing: {
    github: { enabled: false, configMode: 'inherited' },
    jira:   { enabled: false, configMode: 'inherited' },
  },
});

const EMPTY_ORG_DEFAULTS = (): OrgDefaults => ({
  notifications: {},
  ticketing: {},
});

/** Maps frontend ServiceId to the backend ServiceType enum. */
function toServiceType(serviceId: ServiceId): MonitoringServiceType {
  if (serviceId === 'lambda')          return 'LAMBDA';
  if (serviceId === 'glue')            return 'GLUE';
  if (serviceId === 'codepipeline')    return 'PIPELINE';
  if (serviceId === 'step-functions')  return 'STEP_FUNCTIONS';
  if (serviceId === 'ecs')             return 'ECS_SERVICE';
  if (serviceId === 'rds')             return 'RDS_INSTANCE';
  if (serviceId === 'api-gateway')     return 'API_GATEWAY';
  if (serviceId === 'ec2')             return 'EC2_INSTANCE';
  if (serviceId === 'cognito')         return 'COGNITO';
  if (serviceId === 'appsync')         return 'APPSYNC';
  if (serviceId === 'dynamodb')        return 'DYNAMODB';
  if (serviceId === 'sqs')             return 'SQS';
  if (serviceId === 'eventbridge')     return 'EVENTBRIDGE';
  if (serviceId === 'alb')             return 'ALB';
  if (serviceId === 'sns')             return 'SNS';
  if (serviceId === 'cloudfront')      return 'CLOUDFRONT';
  return 'LAMBDA';
}

// ── Backend response shapes ────────────────────────────────────────────────────

interface ServicePolicyResponse {
  serviceType: string;
  policy:      ServicePolicy | null;
  updatedAt:   string | null;
}

interface ItemPolicyResponse {
  serviceType: string;
  resourceId:  string;
  policy:      ItemPolicy | null;
  updatedAt:   string | null;
}

interface ItemPoliciesListResponse {
  serviceType: string;
  policies: Array<{ resourceId: string; policy: ItemPolicy; updatedAt: string }>;
}

@Injectable({ providedIn: 'root' })
export class CloudService {
  private readonly base             = `${environment.apiUrl}/api/v1/cloud/aws`;
  private readonly monitorBase      = `${environment.apiUrl}/api/v1/integrations/aws/monitoring`;
  private readonly azureBase        = `${environment.apiUrl}/api/v1/cloud/azure`;
  private readonly azureMonitorBase = `${environment.apiUrl}/api/v1/integrations/azure/monitoring`;
  private readonly gcpBase          = `${environment.apiUrl}/api/v1/cloud/gcp`;
  private readonly gcpMonitorBase   = `${environment.apiUrl}/api/v1/integrations/gcp/monitoring`;

  // ── Policy state — all backed by backend, populated via load methods ─────────
  private readonly servicePolicies$ =
    new BehaviorSubject<Partial<Record<ServiceId, ServicePolicy>>>({});

  private readonly itemPolicies$ =
    new BehaviorSubject<Partial<Record<ServiceId, Record<string, ItemPolicy>>>>({});

  private readonly orgDefaults$ =
    new BehaviorSubject<OrgDefaults>(EMPTY_ORG_DEFAULTS());

  // ── Org members — cached for member picker in settings drawers ──────────────
  private readonly orgMembers$ = new BehaviorSubject<MemberWithUser[]>([]);

  // ── Monitoring state (backed by backend, populated on demand) ──────────────
  private readonly serviceEntries$ = new BehaviorSubject<ServiceEntry[]>([]);
  private readonly quotaInfo$      = new BehaviorSubject<QuotaInfo | null>(null);

  // ── Resource-list TTL cache ─────────────────────────────────────────────────
  // 5-minute cache for the cloud resource lists that back the overview cards and
  // the service detail pages. Keys are namespaced by the active connection
  // context (account + region) so switching accounts/regions never reuses stale
  // data; the cache is cleared whenever that context changes. Manual refresh
  // passes `bypass = true` to force a fresh fetch and re-seed the entry.
  private readonly CACHE_TTL_MS = 5 * 60 * 1000;
  private readonly cache = new Map<string, { value: unknown; fetchedAt: number }>();
  private cacheContext = 'aws:unknown';
  private currentScope = 'us-east-1';
  private currentAccount = '';

  // ── Shared AWS connection stream ────────────────────────────────────────────
  // The connection (incl. its accounts[]) is fetched once and shared (replayed)
  // across every Cloud-page consumer, so mounting the overview/detail pages does
  // not re-hit the API each time. `refreshConnection()` re-fetches and pushes the
  // new value to live subscribers — used after add-account / per-account region
  // edits on the Integrations page so the account + region selectors update
  // without a full reload. refCount:false keeps the replay buffer warm between
  // visits; the inner catch keeps the shared stream alive across transient errors.
  private readonly connectionTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly connection$: Observable<{ conn: AwsConnection | null; error: boolean }> =
    this.connectionTrigger$.pipe(
      switchMap(() => this.aws.getConnection().pipe(
        map(conn => ({ conn, error: false })),
        catchError(() => of({ conn: null, error: true })),
      )),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

  /** The provider whose Cloud overview is currently active (bound to ?provider=). */
  private activeProvider: CloudProvider = 'AWS';

  constructor(
    private readonly aws:   AwsService,
    private readonly azure: AzureService,
    private readonly gcp:   GcpService,
    private readonly http:  HttpClient,
    private readonly org:   OrgService,
  ) {}

  setProvider(provider: CloudProvider): void { this.activeProvider = provider; }
  getProvider(): CloudProvider { return this.activeProvider; }

  /** The Azure integration state (subscriptions + service-principal status). */
  getAzureConnection() { return this.azure.getConnection(); }

  /** The GCP integration state (projects + service-account status). */
  getGcpConnection() { return this.gcp.getConnection(); }

  /**
   * Azure resource inventory (live Azure Resource Graph discovery) for a service
   * type in the active subscription + location scope. The `error` field is
   * preserved so detail pages can explain an empty list (auth/transport) rather
   * than silently showing nothing — the Azure analog of getCognitoUserPoolsResult.
   */
  getAzureResourcesResult(serviceType: string): Observable<{ resources: AzureResource[]; error: string | null }> {
    const params = `?region=${this.currentScope}&accountId=${this.currentAccount}`;
    return this.http
      .get<{ resources: AzureResource[]; error?: string }>(`${this.azureBase}/${serviceType}/resources${params}`)
      .pipe(
        map(res => ({ resources: res.resources ?? [], error: res.error ?? null })),
        catchError(() => of({ resources: [] as AzureResource[], error: 'Could not reach the Azure discovery endpoint.' })),
      );
  }

  /** Resource list only (drops the error) — used by detail pages. */
  getAzureResources(serviceType: string): Observable<AzureResource[]> {
    return this.getAzureResourcesResult(serviceType).pipe(map(r => r.resources));
  }

  /**
   * Resource counts for EVERY Azure service type in the active subscription +
   * location, from ONE batched backend query (a single Resource Graph call).
   * The overview grid uses this instead of one request per card so opening the
   * Azure overview never bursts ~20 Resource Graph queries (avoids 429).
   */
  getAzureResourceCounts(): Observable<Record<string, number>> {
    const params = `?region=${this.currentScope}&accountId=${this.currentAccount}`;
    return this.http
      .get<{ counts: Record<string, number> }>(`${this.azureBase}/resource-counts${params}`)
      .pipe(map(res => res.counts ?? {}), catchError(() => of({})));
  }

  /**
   * Whether an Azure resource is currently monitored in the active scope. Reads
   * the same serviceEntries pool as AWS (the backend /services list returns
   * every provider's entries, provider-tagged), filtered to AZURE. A global
   * (account-scoped) entry matches any location.
   */
  isAzureMonitored(serviceType: string, resourceId: string, subscriptionId?: string, location?: string): boolean {
    return this.serviceEntries$.value.some(e =>
      e.provider === 'AZURE' && e.serviceType === (serviceType as MonitoringServiceType) && e.resourceId === resourceId && e.isMonitored &&
      (!subscriptionId || !e.accountId || e.accountId === subscriptionId) &&
      (location === undefined || e.region === GLOBAL_SCOPE || e.region === undefined || e.region === location),
    );
  }

  /**
   * Toggles monitoring for an Azure resource via the Azure monitoring endpoint
   * (provider-scoped; enforces quota on enable). Mirrors toggleMonitoring for
   * AWS. `location` defaults to the active concrete location ('all'/'global' are
   * not concrete — the backend then resolves the subscription default / global).
   */
  toggleAzureMonitoring(
    serviceType: string,
    resourceId: string,
    isMonitored: boolean,
    location?: string,
    metadata: Record<string, string> = {},
  ): Observable<{ isMonitored: boolean; region: string; accountId: string; quota: QuotaInfo }> {
    const effLocation = location ?? (this.currentScope !== 'all' && this.currentScope !== 'global' ? this.currentScope : undefined);
    return this.http
      .patch<{ isMonitored: boolean; region: string; accountId: string; quota: QuotaInfo }>(
        `${this.azureMonitorBase}/${serviceType}/toggle`,
        { resourceId, isMonitored, metadata, region: effLocation, subscriptionId: this.currentAccount },
      )
      .pipe(
        tap(res => {
          const entries = this.serviceEntries$.value.filter(
            e => !(e.provider === 'AZURE' && e.serviceType === (serviceType as MonitoringServiceType) && e.resourceId === resourceId &&
              (e.region === undefined || e.region === res.region)),
          );
          entries.push({
            userId: '', provider: 'AZURE',
            serviceType: serviceType as MonitoringServiceType,
            resourceId, region: res.region, accountId: res.accountId,
            isMonitored: res.isMonitored, metadata,
            createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
          });
          this.serviceEntries$.next(entries);
          this.quotaInfo$.next(res.quota);
        }),
      );
  }

  /**
   * GCP resource inventory (live GCP discovery) for a service type in the active
   * project + region scope. The `error` field is preserved so detail pages can
   * explain an empty list (auth/transport) rather than silently showing nothing
   * — the GCP analog of getAzureResourcesResult.
   */
  getGcpResourcesResult(serviceType: string): Observable<{ resources: GcpResource[]; error: string | null }> {
    const params = `?region=${this.currentScope}&accountId=${this.currentAccount}`;
    return this.http
      .get<{ resources: GcpResource[]; error?: string }>(`${this.gcpBase}/${serviceType}/resources${params}`)
      .pipe(
        map(res => ({ resources: res.resources ?? [], error: res.error ?? null })),
        catchError(() => of({ resources: [] as GcpResource[], error: 'Could not reach the GCP discovery endpoint.' })),
      );
  }

  /** Resource list only (drops the error) — used by detail pages. */
  getGcpResources(serviceType: string): Observable<GcpResource[]> {
    return this.getGcpResourcesResult(serviceType).pipe(map(r => r.resources));
  }

  /**
   * Resource counts for EVERY GCP service type in the active project + region,
   * from ONE batched backend query. The overview grid uses this instead of one
   * request per card so opening the GCP overview never bursts ~12 queries.
   */
  getGcpResourceCounts(): Observable<Record<string, number>> {
    const params = `?region=${this.currentScope}&accountId=${this.currentAccount}`;
    return this.http
      .get<{ counts: Record<string, number> }>(`${this.gcpBase}/resource-counts${params}`)
      .pipe(map(res => res.counts ?? {}), catchError(() => of({})));
  }

  /**
   * Whether a GCP resource is currently monitored in the active scope. Reads the
   * same serviceEntries pool as AWS/Azure (the backend /services list returns
   * every provider's entries, provider-tagged), filtered to GCP. A global
   * (project-scoped) entry matches any region.
   */
  isGcpMonitored(serviceType: string, resourceId: string, projectId?: string, region?: string): boolean {
    return this.serviceEntries$.value.some(e =>
      e.provider === 'GCP' && e.serviceType === (serviceType as MonitoringServiceType) && e.resourceId === resourceId && e.isMonitored &&
      (!projectId || !e.accountId || e.accountId === projectId) &&
      (region === undefined || e.region === GLOBAL_SCOPE || e.region === undefined || e.region === region),
    );
  }

  /**
   * Toggles monitoring for a GCP resource via the GCP monitoring endpoint
   * (provider-scoped; enforces quota on enable). Mirrors toggleAzureMonitoring.
   * `region` defaults to the active concrete region ('all'/'global' are not
   * concrete — the backend then resolves the project default / global).
   */
  toggleGcpMonitoring(
    serviceType: string,
    resourceId: string,
    isMonitored: boolean,
    region?: string,
    metadata: Record<string, string> = {},
  ): Observable<{ isMonitored: boolean; region: string; accountId: string; quota: QuotaInfo }> {
    const effRegion = region ?? (this.currentScope !== 'all' && this.currentScope !== 'global' ? this.currentScope : undefined);
    return this.http
      .patch<{ isMonitored: boolean; region: string; accountId: string; quota: QuotaInfo }>(
        `${this.gcpMonitorBase}/${serviceType}/toggle`,
        { resourceId, isMonitored, metadata, region: effRegion, projectId: this.currentAccount },
      )
      .pipe(
        tap(res => {
          const entries = this.serviceEntries$.value.filter(
            e => !(e.provider === 'GCP' && e.serviceType === (serviceType as MonitoringServiceType) && e.resourceId === resourceId &&
              (e.region === undefined || e.region === res.region)),
          );
          entries.push({
            userId: '', provider: 'GCP',
            serviceType: serviceType as MonitoringServiceType,
            resourceId, region: res.region, accountId: res.accountId,
            isMonitored: res.isMonitored, metadata,
            createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
          });
          this.serviceEntries$.next(entries);
          this.quotaInfo$.next(res.quota);
        }),
      );
  }

  /**
   * Re-fetches the shared AWS connection and pushes it to any live subscriber.
   * Call after the connected accounts or their regions change (add-account,
   * per-account region edit, disconnect) so the Cloud page reflects it without a
   * full reload.
   */
  refreshConnection(): void {
    this.connectionTrigger$.next();
  }

  /**
   * Namespace the cache by ACCOUNT only; clear it when the account changes. The
   * region scope is part of each individual cache key (see `cached`), so
   * per-region entries coexist — switching regions does not thrash the cache and
   * an "all regions" aggregate is cached independently of each single region.
   */
  private updateCacheContext(conn: AwsConnection | null): void {
    const key = conn && conn.connectionStatus === 'ACTIVE'
      ? `aws:${conn.primaryAccountId ?? conn.accountId ?? '-'}`
      : 'aws:none';
    if (key !== this.cacheContext) {
      this.cacheContext = key;
      this.cache.clear();
    }
  }

  /** Sets the active region scope for subsequent resource fetches: a region code, 'all', or 'global'. */
  setScope(scope: string): void { this.currentScope = scope; }
  /** The active region scope. */
  getScope(): string { return this.currentScope; }

  /** Sets the active AWS account for subsequent resource fetches (its id is part of every cache key + request). */
  setAccount(accountId: string): void { this.currentAccount = accountId; }
  /** The active AWS account id. */
  getAccount(): string { return this.currentAccount; }

  /**
   * The connected AWS accounts for a connection. The backend always returns the
   * full first-class `accounts` array (per-account records); the account selector
   * and all account-scoped fetches read from this list.
   */
  getAwsAccounts(conn: AwsConnection): AwsAccount[] {
    return conn.accounts ?? [];
  }

  /**
   * TTL cache wrapper, scoped by region. Returns the cached value when fresh
   * (and not bypassed), otherwise runs `httpFetch`. Only successful responses
   * are cached — transport errors fall back to `fallback` and are NOT stored, so
   * a failed request never poisons the cache for 5 minutes. `scope` is the
   * region scope ('all', a specific region, or 'global') and is part of the key
   * so manual refresh of one region scope bypasses only that entry.
   */
  private cached<T>(resourceKey: string, scope: string, httpFetch: () => Observable<T>, fallback: T, bypass = false): Observable<T> {
    const key = `${this.cacheContext}:${this.currentAccount}:${scope}:${resourceKey}`;
    const hit = this.cache.get(key);
    if (!bypass && hit && Date.now() - hit.fetchedAt < this.CACHE_TTL_MS) {
      return of(hit.value as T);
    }
    return httpFetch().pipe(
      tap(value => this.cache.set(key, { value, fetchedAt: Date.now() })),
      catchError(() => of(fallback)),
    );
  }

  /** When was the given resource list (in a region scope) last fetched (epoch ms), or null if uncached. */
  getResourceListLastUpdated(resourceKey: string, scope: string = this.currentScope): number | null {
    return this.cache.get(`${this.cacheContext}:${this.currentAccount}:${scope}:${resourceKey}`)?.fetchedAt ?? null;
  }

  /** Invalidate a single cached resource list (used by manual refresh paths). */
  invalidateResourceList(resourceKey: string, scope: string = this.currentScope): void {
    this.cache.delete(`${this.cacheContext}:${this.currentAccount}:${scope}:${resourceKey}`);
  }

  getConnectionState(): Observable<{ state: CloudPageState; connection: AwsConnection | null }> {
    return this.connection$.pipe(
      map(({ conn, error }) => {
        if (error) return { state: 'error' as CloudPageState, connection: null };
        this.updateCacheContext(conn);
        if (!conn || conn.connectionStatus !== 'ACTIVE') {
          return { state: 'not-connected' as CloudPageState, connection: conn };
        }
        return { state: 'connected' as CloudPageState, connection: conn };
      }),
    );
  }

  buildSummary(conn: AwsConnection): CloudSummary {
    const accounts = this.getAwsAccounts(conn);
    // Resolve the active account: the currently-selected one if it's still
    // connected, otherwise the first available account.
    const current = accounts.find(a => a.accountId === this.currentAccount) ?? accounts[0];
    return {
      accountId:      current?.accountId ?? conn.accountId ?? '—',
      account:        current?.accountId ?? conn.accountId ?? '—',
      accounts,
      roleArn:        conn.roleArn    ?? '—',
      region:         this.currentScope,
      scope:          this.currentScope,
      defaultRegion:  current?.defaultRegion  ?? 'us-east-1',
      enabledRegions: current?.enabledRegions ?? ['us-east-1'],
      lastSyncAt:     conn.lastTestedAt ?? conn.updatedAt ?? new Date().toISOString(),
      services:       CONNECTED_SERVICES,
    };
  }

  /** Returns updated service cards with real resource counts, sorted alphabetically by name. */
  getLambdaFunctions(bypass = false): Observable<LambdaFunction[]> {
    return this.cached('lambda', this.currentScope, () =>
      this.http.get<{ functions: LambdaFunction[] }>(`${this.base}/lambda/functions?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.functions ?? [])),
      [], bypass);
  }

  getLambdaMetrics(): Observable<LambdaMetrics | null> {
    return this.http.get<{ metrics: LambdaMetrics | null }>(`${this.base}/lambda/metrics`).pipe(
      map(res => res.metrics ?? null),
      catchError(() => of(null)),
    );
  }

  getLambdaFunctionMetrics(name: string): Observable<LambdaFunctionMetrics | null> {
    return this.http
      .get<{ metrics: LambdaFunctionMetrics | null }>(
        `${this.base}/lambda/function/${encodeURIComponent(name)}/metrics`,
      )
      .pipe(map(res => res.metrics ?? null));
  }

  getGlueJobs(bypass = false): Observable<GlueJob[]> {
    return this.cached('glue', this.currentScope, () =>
      this.http.get<{ jobs: GlueJob[] }>(`${this.base}/glue/jobs?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.jobs ?? [])),
      [], bypass);
  }

  getGlueJobRuns(jobName: string): Observable<GlueJobRun[]> {
    return this.http
      .get<{ runs: GlueJobRun[] }>(
        `${this.base}/glue/job/${encodeURIComponent(jobName)}/runs`,
      )
      .pipe(map(res => res.runs ?? []));
  }

  getPipelines(bypass = false): Observable<Pipeline[]> {
    return this.cached('codepipeline', this.currentScope, () =>
      this.http.get<{ pipelines: Pipeline[] }>(`${this.base}/codepipeline/pipelines?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.pipelines ?? [])),
      [], bypass);
  }

  getPipelineExecutions(name: string): Observable<PipelineExecution[]> {
    return this.http
      .get<{ executions: PipelineExecution[] }>(
        `${this.base}/codepipeline/pipeline/${encodeURIComponent(name)}/executions`,
      )
      .pipe(map(res => res.executions ?? []));
  }

  getStateMachines(bypass = false): Observable<StateMachine[]> {
    return this.cached('step-functions', this.currentScope, () =>
      this.http.get<{ stateMachines: StateMachine[] }>(`${this.base}/step-functions/state-machines?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.stateMachines ?? [])),
      [], bypass);
  }

  getEcsServices(bypass = false): Observable<EcsService[]> {
    return this.cached('ecs', this.currentScope, () =>
      this.http.get<{ services: EcsService[] }>(`${this.base}/ecs/services?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.services ?? [])),
      [], bypass);
  }

  getRdsInstances(bypass = false): Observable<RdsInstance[]> {
    return this.cached('rds', this.currentScope, () =>
      this.http.get<{ instances: RdsInstance[] }>(`${this.base}/rds/instances?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.instances ?? [])),
      [], bypass);
  }

  getApiGateways(bypass = false): Observable<ApiGatewayApi[]> {
    return this.cached('api-gateway', this.currentScope, () =>
      this.http.get<{ apis: ApiGatewayApi[] }>(`${this.base}/api-gateway/apis?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.apis ?? [])),
      [], bypass);
  }

  getEc2Instances(bypass = false): Observable<Ec2Instance[]> {
    return this.cached('ec2', this.currentScope, () =>
      this.http.get<{ instances: Ec2Instance[] }>(`${this.base}/ec2/instances?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.instances ?? [])),
      [], bypass);
  }

  getCognitoUserPools(bypass = false): Observable<CognitoUserPool[]> {
    return this.cached('cognito', this.currentScope, () =>
      this.http.get<{ userPools: CognitoUserPool[] }>(`${this.base}/cognito/user-pools?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.userPools ?? [])),
      [], bypass);
  }

  /**
   * Like getCognitoUserPools() but preserves the backend `error` field (e.g. an
   * AccessDenied from a cross-account role missing cognito-idp:ListUserPools, or
   * a transport failure) so the detail page can explain an empty list instead of
   * silently showing nothing.
   */
  getCognitoUserPoolsResult(bypass = false): Observable<{ userPools: CognitoUserPool[]; error: string | null }> {
    return this.cached('cognito-result', this.currentScope, () =>
      this.http.get<{ userPools: CognitoUserPool[]; error?: string }>(`${this.base}/cognito/user-pools?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(
        map(res => ({ userPools: res.userPools ?? [], error: res.error ?? null })),
      ),
      { userPools: [], error: 'Could not reach the Cognito discovery endpoint. The backend may not be deployed yet.' },
      bypass);
  }

  getAppSyncApis(bypass = false): Observable<AppSyncApi[]> {
    return this.cached('appsync', this.currentScope, () =>
      this.http.get<{ apis: AppSyncApi[] }>(`${this.base}/appsync/apis?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.apis ?? [])),
      [], bypass);
  }

  getDynamoDbTables(bypass = false): Observable<DynamoDbTable[]> {
    return this.cached('dynamodb', this.currentScope, () =>
      this.http.get<{ tables: DynamoDbTable[] }>(`${this.base}/dynamodb/tables?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.tables ?? [])),
      [], bypass);
  }

  getSqsQueues(bypass = false): Observable<SqsQueue[]> {
    return this.cached('sqs', this.currentScope, () =>
      this.http.get<{ queues: SqsQueue[] }>(`${this.base}/sqs/queues?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.queues ?? [])),
      [], bypass);
  }

  getEventBridgeRules(bypass = false): Observable<EventBridgeRule[]> {
    return this.cached('eventbridge', this.currentScope, () =>
      this.http.get<{ rules: EventBridgeRule[] }>(`${this.base}/eventbridge/rules?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.rules ?? [])),
      [], bypass);
  }

  getAlbLoadBalancers(bypass = false): Observable<AlbLoadBalancer[]> {
    return this.cached('alb', this.currentScope, () =>
      this.http.get<{ loadBalancers: AlbLoadBalancer[] }>(`${this.base}/alb/load-balancers?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.loadBalancers ?? [])),
      [], bypass);
  }

  getSnsTopics(bypass = false): Observable<SnsTopic[]> {
    return this.cached('sns', this.currentScope, () =>
      this.http.get<{ topics: SnsTopic[] }>(`${this.base}/sns/topics?region=${this.currentScope}&accountId=${this.currentAccount}`).pipe(map(res => res.topics ?? [])),
      [], bypass);
  }

  getCloudFrontDistributions(bypass = false): Observable<CloudFrontDistribution[]> {
    return this.cached('cloudfront', 'global', () =>
      this.http.get<{ distributions: CloudFrontDistribution[] }>(`${this.base}/cloudfront/distributions?region=global&accountId=${this.currentAccount}`).pipe(map(res => res.distributions ?? [])),
      [], bypass);
  }

  /**
   * Per-resource CloudWatch metric history (last 24h, hourly) for the newer
   * metric-driven service types. Returns null on error so drill-down panels can
   * show a graceful empty/error state.
   */
  getResourceMetricHistory(serviceId: ServiceId, resourceId: string): Observable<MetricHistory | null> {
    const path = this.metricHistoryPath(serviceId, resourceId);
    if (!path) return of(null);
    return this.http.get<{ metrics: MetricHistory | null }>(path).pipe(
      map(res => res.metrics ?? null),
      catchError(() => of(null)),
    );
  }

  private metricHistoryPath(serviceId: ServiceId, resourceId: string): string | null {
    const enc = encodeURIComponent(resourceId);
    switch (serviceId) {
      case 'api-gateway': return `${this.base}/api-gateway/api/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'ec2':         return `${this.base}/ec2/instance/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'cognito':     return `${this.base}/cognito/user-pool/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'appsync':     return `${this.base}/appsync/api/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'dynamodb':    return `${this.base}/dynamodb/table/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'sqs':         return `${this.base}/sqs/queue/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'eventbridge': return `${this.base}/eventbridge/rule/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'alb':         return `${this.base}/alb/load-balancer/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'sns':         return `${this.base}/sns/topic/${enc}/metrics?region=${this.currentScope}&accountId=${this.currentAccount}`;
      case 'cloudfront':  return `${this.base}/cloudfront/distribution/${enc}/metrics?region=global&accountId=${this.currentAccount}`;
      default:            return null;
    }
  }

  getStateMachineExecutions(arn: string): Observable<StateMachineExecution[]> {
    return this.http
      .get<{ executions: StateMachineExecution[] }>(
        `${this.base}/step-functions/state-machine/executions?arn=${encodeURIComponent(arn)}`,
      )
      .pipe(map(res => res.executions ?? []));
  }

  getEcsServiceEvents(serviceArn: string): Observable<EcsServiceEvent[]> {
    return this.http
      .get<{ events: EcsServiceEvent[] }>(
        `${this.base}/ecs/service/events?serviceArn=${encodeURIComponent(serviceArn)}`,
      )
      .pipe(map(res => res.events ?? []));
  }

  getRdsInstanceEvents(dbInstanceIdentifier: string): Observable<RdsInstanceEvent[]> {
    return this.http
      .get<{ events: RdsInstanceEvent[] }>(
        `${this.base}/rds/instance/events?dbInstanceIdentifier=${encodeURIComponent(dbInstanceIdentifier)}`,
      )
      .pipe(map(res => res.events ?? []));
  }

  // ── Policy loading ────────────────────────────────────────────────────────────

  /**
   * Loads org defaults and all service-level policies from the backend.
   * Call this once when the Cloud hub connects (cloud-hub-shell.component.ts).
   * After this resolves, configSummary() returns truthful counts.
   */
  loadPolicies(): Observable<void> {
    return new Observable<void>(observer => {
      Promise.all([
        this.http.get<OrgDefaults>(`${this.monitorBase}/org-defaults`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/LAMBDA/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/GLUE/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/PIPELINE/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/STEP_FUNCTIONS/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/ECS_SERVICE/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/RDS_INSTANCE/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/API_GATEWAY/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/EC2_INSTANCE/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/COGNITO/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/APPSYNC/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/DYNAMODB/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/SQS/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/EVENTBRIDGE/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/ALB/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/SNS/policy`).toPromise(),
        this.http.get<ServicePolicyResponse>(`${this.monitorBase}/CLOUDFRONT/policy`).toPromise(),
      ]).then(([orgDefaults, lambdaRes, glueRes, pipelineRes, stepFunctionsRes, ecsRes, rdsRes, apiGatewayRes, ec2Res, cognitoRes, appsyncRes, dynamodbRes, sqsRes, eventbridgeRes, albRes, snsRes, cloudfrontRes]) => {
        if (orgDefaults) this.orgDefaults$.next(orgDefaults);

        const policies: Partial<Record<ServiceId, ServicePolicy>> = {};
        if (lambdaRes?.policy)        policies['lambda']          = lambdaRes.policy;
        if (glueRes?.policy)          policies['glue']            = glueRes.policy;
        if (pipelineRes?.policy)      policies['codepipeline']    = pipelineRes.policy;
        if (stepFunctionsRes?.policy) policies['step-functions']  = stepFunctionsRes.policy;
        if (ecsRes?.policy)           policies['ecs']             = ecsRes.policy;
        if (rdsRes?.policy)           policies['rds']             = rdsRes.policy;
        if (apiGatewayRes?.policy)    policies['api-gateway']     = apiGatewayRes.policy;
        if (ec2Res?.policy)           policies['ec2']             = ec2Res.policy;
        if (cognitoRes?.policy)       policies['cognito']         = cognitoRes.policy;
        if (appsyncRes?.policy)       policies['appsync']         = appsyncRes.policy;
        if (dynamodbRes?.policy)      policies['dynamodb']        = dynamodbRes.policy;
        if (sqsRes?.policy)           policies['sqs']             = sqsRes.policy;
        if (eventbridgeRes?.policy)   policies['eventbridge']     = eventbridgeRes.policy;
        if (albRes?.policy)           policies['alb']             = albRes.policy;
        if (snsRes?.policy)           policies['sns']             = snsRes.policy;
        if (cloudfrontRes?.policy)    policies['cloudfront']      = cloudfrontRes.policy;
        this.servicePolicies$.next(policies);

        observer.next();
        observer.complete();
      }).catch(err => {
        console.warn('[cloud] failed to load policies', err);
        observer.next();
        observer.complete();
      });
    });
  }

  /**
   * Loads all item-level policies for a service from the backend.
   * Call this in each detail component's ngOnInit so item override summaries
   * and drawer state are available synchronously when a drawer opens.
   */
  loadItemPoliciesForService(serviceId: ServiceId): Observable<void> {
    const serviceType = toServiceType(serviceId);
    return this.http
      .get<ItemPoliciesListResponse>(`${this.monitorBase}/${serviceType}/policies`)
      .pipe(
        tap(res => {
          const current = { ...this.itemPolicies$.value };
          const map: Record<string, ItemPolicy> = {};
          for (const { resourceId, policy } of res.policies ?? []) {
            map[resourceId] = policy;
          }
          current[serviceId] = map;
          this.itemPolicies$.next(current);
        }),
        map(() => void 0),
        catchError(err => {
          console.warn('[cloud] failed to load item policies', { serviceId, err });
          return of(void 0);
        }),
      );
  }

  // ── Org defaults ─────────────────────────────────────────────────────────────

  getOrgDefaults(): OrgDefaults {
    return this.orgDefaults$.value;
  }

  // ── Service policy ────────────────────────────────────────────────────────────

  getServicePolicy(id: ServiceId): ServicePolicy {
    return this.servicePolicies$.value[id] ?? EMPTY_SERVICE_POLICY();
  }

  saveServicePolicy(id: ServiceId, policy: ServicePolicy): Observable<void> {
    const serviceType = toServiceType(id);
    return this.http
      .put<ServicePolicyResponse>(`${this.monitorBase}/${serviceType}/policy`, policy)
      .pipe(
        tap(res => {
          if (res.policy) {
            const current = { ...this.servicePolicies$.value };
            current[id] = res.policy;
            this.servicePolicies$.next(current);
          }
        }),
        map(() => void 0),
      );
  }

  // ── Item policy ───────────────────────────────────────────────────────────────

  getItemPolicy(serviceId: ServiceId, itemId: string): ItemPolicy | undefined {
    return this.itemPolicies$.value[serviceId]?.[itemId];
  }

  saveItemPolicy(serviceId: ServiceId, itemId: string, policy: ItemPolicy): Observable<void> {
    const serviceType = toServiceType(serviceId);
    return this.http
      .put<ItemPolicyResponse>(
        `${this.monitorBase}/${serviceType}/resource-policy`,
        { resourceId: itemId, ...policy },
      )
      .pipe(
        tap(res => {
          if (res.policy) {
            const current  = { ...this.itemPolicies$.value };
            const existing = { ...(current[serviceId] ?? {}) };
            existing[itemId] = res.policy;
            current[serviceId] = existing;
            this.itemPolicies$.next(current);
          }
        }),
        map(() => void 0),
      );
  }

  /** Hard-deletes the item policy record. After this the item fully defers to service level. */
  deleteItemPolicy(serviceId: ServiceId, itemId: string): Observable<void> {
    const serviceType = toServiceType(serviceId);
    return this.http
      .delete<unknown>(
        `${this.monitorBase}/${serviceType}/resource-policy?resourceId=${encodeURIComponent(itemId)}`,
      )
      .pipe(
        tap(() => {
          const current  = { ...this.itemPolicies$.value };
          const existing = { ...(current[serviceId] ?? {}) };
          delete existing[itemId];
          current[serviceId] = existing;
          this.itemPolicies$.next(current);
        }),
        map(() => void 0),
      );
  }

  /**
   * Hard-deletes all item policy records for a service.
   * After this call all items fully defer to the service-level policy.
   */
  purgeAllItemPolicies(serviceId: ServiceId): Observable<void> {
    const serviceType = toServiceType(serviceId);
    return this.http
      .delete<unknown>(`${this.monitorBase}/${serviceType}/policies`)
      .pipe(
        tap(() => {
          const current = { ...this.itemPolicies$.value };
          current[serviceId] = {};
          this.itemPolicies$.next(current);
        }),
        map(() => void 0),
      );
  }

  // ── Resolution helpers ────────────────────────────────────────────────────────
  //
  // Resolution order for each channel:
  //   1. item 'enabled-custom'    → item's own overrideConfig
  //   2. item 'disabled'          → disabled
  //   3. item 'enabled-inherited' → resolve destination from service → org (enabled=true)
  //   4. item 'service' or absent → fully defer to service policy:
  //       a. service disabled     → disabled
  //       b. service custom       → service's overrideConfig
  //       c. service inherited    → org default

  private resolveChannel<
    TSvc extends { enabled: boolean; configMode: ConfigMode; overrideConfig?: TDest },
    TItm extends { mode: ItemChannelMode; overrideConfig?: TDest },
    TDest,
  >(
    svc:       TSvc,
    itm:       TItm | undefined,
    orgConfig: TDest | undefined,
  ): ResolvedChannelState<TDest> {
    if (itm?.mode === 'disabled') {
      return { enabled: false, source: null, config: null, isConfigMissing: false };
    }
    if (itm?.mode === 'enabled-custom') {
      const config = itm.overrideConfig ?? null;
      return { enabled: true, source: 'item', config, isConfigMissing: config === null };
    }
    // 'enabled-inherited' or 'service' (or no item record) → resolve from service
    if (!svc.enabled) {
      // Item explicitly opted into enabled-inherited but service is off → enabled but no destination
      if (itm?.mode === 'enabled-inherited') {
        return { enabled: true, source: null, config: null, isConfigMissing: true };
      }
      return { enabled: false, source: null, config: null, isConfigMissing: false };
    }
    if (svc.configMode === 'custom') {
      const config = svc.overrideConfig ?? null;
      return { enabled: true, source: 'service', config, isConfigMissing: config === null };
    }
    // service inherited → org
    const config = orgConfig ?? null;
    return { enabled: true, source: 'org', config, isConfigMissing: config === null };
  }

  /** Returns the fully resolved effective policy for a service entry (itemId) or a service (pass null). */
  resolveEffectivePolicy(serviceId: ServiceId, itemId: string | null): ResolvedPolicy {
    const svc = this.servicePolicies$.value[serviceId] ?? EMPTY_SERVICE_POLICY();
    const itm = itemId ? this.itemPolicies$.value[serviceId]?.[itemId] : undefined;
    const org = this.orgDefaults$.value;

    return {
      notifications: {
        slack: this.resolveChannel(svc.notifications.slack, itm?.notifications.slack, org.notifications.slack),
        teams: this.resolveChannel(svc.notifications.teams, itm?.notifications.teams, org.notifications.teams),
        email: this.resolveChannel(svc.notifications.email, itm?.notifications.email, org.notifications.email),
      },
      ticketing: {
        github: this.resolveChannel(svc.ticketing.github, itm?.ticketing.github, org.ticketing.github),
        jira:   this.resolveChannel(svc.ticketing.jira,   itm?.ticketing.jira,   org.ticketing.jira),
      },
    };
  }

  /** Convenience wrapper — resolves effective state for a single notification channel. */
  getEffectiveNotificationState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: 'slack',
  ): ResolvedChannelState<SlackDestination>;
  getEffectiveNotificationState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: 'teams',
  ): ResolvedChannelState<TeamsDestination>;
  getEffectiveNotificationState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: 'email',
  ): ResolvedChannelState<EmailDestination>;
  getEffectiveNotificationState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: keyof ServiceNotificationPolicy,
  ): ResolvedChannelState<SlackDestination | TeamsDestination | EmailDestination> {
    return this.resolveEffectivePolicy(serviceId, itemId).notifications[channel];
  }

  /** Convenience wrapper — resolves effective state for a single ticketing channel. */
  getEffectiveTicketingState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: 'github',
  ): ResolvedChannelState<GitHubDestination>;
  getEffectiveTicketingState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: 'jira',
  ): ResolvedChannelState<JiraDestination>;
  getEffectiveTicketingState(
    serviceId: ServiceId,
    itemId: string | null,
    channel: keyof ServiceTicketingPolicy,
  ): ResolvedChannelState<GitHubDestination | JiraDestination> {
    return this.resolveEffectivePolicy(serviceId, itemId).ticketing[channel];
  }

  // ── Summary helpers ───────────────────────────────────────────────────────────

  /**
   * Human-readable summary of a service's enabled channels (used in service cards).
   * Returns '' until loadPolicies() resolves — ensuring no mock-backed counts appear.
   */
  configSummary(id: string): string {
    const sid    = id as ServiceId;
    const policy = this.servicePolicies$.value[sid];
    if (!policy) return '';
    const channels = (['slack', 'teams', 'email'] as const).filter(k => policy.notifications[k].enabled).length;
    const tickets  = (['github', 'jira']           as const).filter(k => policy.ticketing[k].enabled).length;
    if (channels === 0 && tickets === 0) return '';
    const parts: string[] = [];
    if (channels) parts.push(`${channels} channel${channels !== 1 ? 's' : ''}`);
    if (tickets)  parts.push(`${tickets} ticket target${tickets !== 1 ? 's' : ''}`);
    return parts.join(', ');
  }

  /** Human-readable summary of which groups a given item overrides (used in detail tables). */
  getItemOverrideSummary(serviceId: string, itemId: string): string {
    const policy = this.itemPolicies$.value[serviceId as ServiceId]?.[itemId];
    if (!policy) return '';
    const hasCustomNotif  = (['slack', 'teams', 'email'] as const)
      .some(k => policy.notifications[k].mode !== 'service');
    const hasCustomTicket = (['github', 'jira'] as const)
      .some(k => policy.ticketing[k].mode !== 'service');
    if (hasCustomNotif && hasCustomTicket) return 'Custom';
    if (hasCustomNotif)  return 'Custom alerts';
    if (hasCustomTicket) return 'Custom ticketing';
    return '';
  }

  // ── Monitoring state ──────────────────────────────────────────────────────────

  /**
   * Loads service entries and quota state from the backend.
   * Call this when a detail page initialises so monitoring indicators are available.
   */
  loadMonitoringState(): Observable<void> {
    return new Observable<void>(observer => {
      Promise.all([
        this.http.get<{ entries: ServiceEntry[] }>(`${this.monitorBase}/services`).toPromise(),
        this.http.get<QuotaInfo>(`${this.monitorBase}/quota`).toPromise(),
      ]).then(([entriesRes, quota]) => {
        this.serviceEntries$.next(entriesRes?.entries ?? []);
        if (quota) this.quotaInfo$.next(quota);
        observer.next();
        observer.complete();
      }).catch(err => {
        console.warn('[cloud] failed to load monitoring state', err);
        observer.next();
        observer.complete();
      });
    });
  }

  /**
   * Loads active org members from the backend and caches them for member pickers
   * in service and item settings drawers. Safe to call multiple times.
   */
  loadOrgMembers(): Observable<void> {
    return this.org.getMyOrgs().pipe(
      take(1),
      switchMap(({ memberships }) => {
        if (!memberships.length) return of(void 0);
        const orgId = memberships[0].org.orgId;
        return this.org.listMembers(orgId).pipe(
          tap(({ members }) => {
            this.orgMembers$.next(members.filter(m => m.membershipStatus === 'active'));
          }),
          map(() => void 0),
        );
      }),
      catchError(err => {
        console.warn('[cloud] failed to load org members', err);
        return of(void 0);
      }),
    );
  }

  /** Returns cached active org members (empty until loadOrgMembers resolves). */
  getOrgMembers(): MemberWithUser[] { return this.orgMembers$.value; }

  /** Returns the current cached quota info (null until loadMonitoringState resolves). */
  getQuotaInfo(): QuotaInfo | null { return this.quotaInfo$.value; }

  /** Observable stream of quota info — subscribe in components that show live quota. */
  quotaInfo(): Observable<QuotaInfo | null> { return this.quotaInfo$.asObservable(); }

  /**
   * Synchronous check against loaded service entries. When `region` is provided,
   * the entry must match that region (a legacy entry without a region matches
   * any region, for backward compatibility). A monitored resource in us-west-2
   * is distinct from the same resource id in us-east-1.
   */
  isMonitored(serviceId: ServiceId, itemId: string, region?: string): boolean {
    const serviceType = toServiceType(serviceId);
    return this.serviceEntries$.value.some(
      e => e.serviceType === serviceType && e.resourceId === itemId && e.isMonitored &&
        (region === undefined || e.region === undefined || e.region === region),
    );
  }

  getMonitoringCount(): number {
    return this.quotaInfo$.value?.currentCount ?? this.serviceEntries$.value.filter(e => e.isMonitored).length;
  }

  /**
   * Count of currently-monitored services within a single account + region scope.
   * Regional services must match `scope`; global (account-scoped) services like
   * CloudFront are always in scope (mirrors the region selector's "global always
   * shown" rule). `scope === ALL_REGIONS_SCOPE` counts every region for the
   * account. Provider-agnostic — the caller passes the active account/region, so
   * the same logic serves Azure once its entries carry an accountId + region.
   */
  scopedMonitoredCount(accountId: string, scope: string, provider?: CloudProvider): number {
    return this.serviceEntries$.value.filter(e => {
      if (!e.isMonitored) return false;
      // Scope to the active provider when requested (AWS + Azure share one pool).
      if (provider && e.provider && e.provider !== provider) return false;
      // Scope to the selected account/subscription when both sides know it.
      if (accountId && e.accountId && e.accountId !== accountId) return false;
      if (e.region === GLOBAL_SCOPE) return true;    // global services are always in scope
      if (scope === ALL_REGIONS_SCOPE) return true;  // all-enabled-regions aggregate
      return e.region === scope;                     // concrete region match
    }).length;
  }

  canEnableMoreMonitoring(): boolean {
    const quota = this.quotaInfo$.value;
    if (!quota) return true;
    return quota.remaining > 0;
  }

  getQuotaUsage(): { currentCount: number; limit: number; remaining: number; percentUsed: number } | null {
    return this.quotaInfo$.value;
  }

  /**
   * Calls the backend toggle endpoint; atomically enforces quota on enable.
   * Updates local service-entry cache on success.
   * Returns the updated quota from the response.
   */
  toggleMonitoring(
    serviceId: ServiceId,
    resourceId: string,
    isMonitored: boolean,
    region?: string,
    metadata: Record<string, string> = {},
  ): Observable<{ isMonitored: boolean; quota: QuotaInfo }> {
    const serviceType = toServiceType(serviceId);
    // Default to the active region scope when it's a concrete region, so toggling
    // in a single-region view monitors the resource in that region. ('all' is not
    // a concrete region — the backend then falls back to the account default.)
    const effRegion = region ?? (this.currentScope !== 'all' ? this.currentScope : undefined);
    return this.http
      .patch<{ isMonitored: boolean; quota: QuotaInfo }>(
        `${this.monitorBase}/${serviceType}/toggle`,
        { resourceId, isMonitored, metadata, region: effRegion },
      )
      .pipe(
        tap(res => {
          // Replace the entry for this (serviceType, resourceId, region) tuple.
          const entries = this.serviceEntries$.value.filter(
            e => !(e.serviceType === serviceType && e.resourceId === resourceId &&
              (region === undefined || e.region === undefined || e.region === region)),
          );
          entries.push({
            userId: '',
            serviceType,
            resourceId,
            region,
            isMonitored: res.isMonitored,
            metadata,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
          this.serviceEntries$.next(entries);
          this.quotaInfo$.next(res.quota);
        }),
      );
  }

  getQuotaExceededMessage(): string {
    const quota    = this.quotaInfo$.value;
    const tier     = quota?.tier  ?? 'FREE';
    const limit    = quota?.limit ?? 0;
    const tierLabel = tier === 'FREE' ? 'Free' : tier === 'PRO' ? 'Pro' : 'Enterprise';
    const nextTier  = tier === 'FREE' ? 'Pro' : tier === 'PRO' ? 'Enterprise' : null;
    let msg = `Quota exceeded: Your ${tierLabel} plan allows up to ${limit} monitored services.`;
    if (nextTier) msg += ` Upgrade to ${nextTier} to monitor more.`;
    return msg;
  }
}
